"use client";

import { c as _c } from "react/compiler-runtime";
import type { AtlasFile } from "@-/prisma";
import { FileDisplayDialog } from "@/components/dialogs/FileDisplayDialog";
import DateDisplay from "@/components/display/DateDisplay";
import type { FileDisplayFile } from "@/components/display/FileDisplay/FileDisplay";
import { FileTypeIcon } from "@/components/display/FileDisplay/FileTypeIcon";
import SelectFilter from "@/components/filters/components/SelectFilter";
import { UploadFileTrigger } from "@/components/forms/media/UploadFileTrigger";
import { SearchInput } from "@/components/patterns/SearchInput";
import { ListPage, TabListSection } from "@/components/shell";
import { Button } from "@/components/ui/Button";
import { FilterType } from "@/constants/filter";
import { useBulkListAction } from "@/hooks/useBulkListAction";
import type { AnyLabeledFilterConfig } from "@/types/list";
import { trpc } from "@/utils/trpc.client";
import multiDownload from "multi-download";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import FilterList from "./FilterList";
import { ListHeaderControls } from "./ListHeaderControls";
const noTagsFilterValue = "__atlas_no_tags__";
type FolderFilters = {
  type: string[];
  tags: string[];
};
type FolderFile = FileDisplayFile & Pick<AtlasFile, "id"> & Omit<Pick<AtlasFile, "documentType" | "id" | "mimetype" | "tags" | "type">, keyof FileDisplayFile> & {
  createdAt: Date | string;
};
function normalizeFolderFilters(filters: Partial<Record<keyof FolderFilters, unknown>>): FolderFilters {
  return {
    type: Array.isArray(filters.type) ? filters.type.filter((value): value is string => typeof value === "string") : [],
    tags: Array.isArray(filters.tags) ? filters.tags.filter((value): value is string => typeof value === "string") : []
  };
}
function buildFolderFilterOptions(values: string[]) {
  return Array.from(new Set(values.filter(Boolean))).sort((left, right) => left.localeCompare(right)).map(value => ({
    label: value,
    value
  }));
}
function fileMatchesSearch(file: FolderFile, search: string) {
  const query = search.trim().toLowerCase();
  if (!query) {
    return true;
  }
  return [file.name, file.type, file.mimetype ?? "", file.documentType ?? "", ...file.tags].some(value => value.toLowerCase().includes(query));
}
export function FolderDashboardBody(t0) {
  const $ = _c(89);
  const {
    canManage: t1,
    files: storedFiles,
    isLoading: t2,
    organizationMembershipId,
    schoolId,
    title
  } = t0;
  const canManage = t1 === undefined ? true : t1;
  const isLoading = t2 === undefined ? false : t2;
  let t3;
  if ($[0] !== storedFiles) {
    t3 = storedFiles.map(_temp2);
    $[0] = storedFiles;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const files = t3;
  const utils = trpc.useUtils();
  const [search, setSearch] = useState("");
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      type: [],
      tags: []
    };
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const [filters, setFilters] = useState(t4);
  const [selectedFile, setSelectedFile] = useState(null);
  let t5;
  if ($[3] !== utils) {
    t5 = {
      onSuccess() {
        utils.file.invalidate();
      }
    };
    $[3] = utils;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const deleteFileMutation = trpc.file.delete.useMutation(t5);
  const deleteManyMutation = trpc.file.deleteMany.useMutation();
  const t6 = canManage && Boolean(organizationMembershipId) && !schoolId;
  let t7;
  if ($[5] !== deleteManyMutation) {
    t7 = ids => deleteManyMutation.mutateAsync({
      ids
    });
    $[5] = deleteManyMutation;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== utils) {
    t8 = () => utils.file.invalidate();
    $[7] = utils;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== t6 || $[10] !== t7 || $[11] !== t8) {
    t9 = {
      action: "Delete",
      noun: "files",
      consequences: "Standalone files in your own folder will be permanently removed from IEBridge. Files attached to schools, projects, notes or applications are excluded. Files still in use or part of saved photo edit history cannot be deleted.",
      enabled: t6,
      getId: _temp3,
      execute: t7,
      invalidate: t8
    };
    $[9] = t6;
    $[10] = t7;
    $[11] = t8;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const bulkDelete = useBulkListAction(t9);
  let t10;
  if ($[13] !== files) {
    t10 = buildFolderFilterOptions(files.map(_temp4));
    $[13] = files;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  const typeOptions = t10;
  let t11;
  if ($[15] !== files) {
    const options = buildFolderFilterOptions(files.flatMap(_temp5));
    const hasFilesWithoutTags = files.some(_temp6);
    t11 = hasFilesWithoutTags ? [{
      label: "No tags",
      value: noTagsFilterValue
    }, ...options] : options;
    $[15] = files;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  const tagOptions = t11;
  let t12;
  if ($[17] !== typeOptions) {
    t12 = {
      type: FilterType.MULTI_SELECT,
      options: typeOptions
    };
    $[17] = typeOptions;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = z.array(z.string()).optional().default([]);
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] !== t12 || $[21] !== typeOptions) {
    t14 = {
      label: "Type",
      type: FilterType.MULTI_SELECT,
      component: "div",
      props: t12,
      schema: t13,
      options: typeOptions
    };
    $[20] = t12;
    $[21] = typeOptions;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== tagOptions) {
    t15 = {
      type: FilterType.MULTI_SELECT,
      options: tagOptions
    };
    $[23] = tagOptions;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = z.array(z.string()).optional().default([]);
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  let t17;
  if ($[26] !== t15 || $[27] !== tagOptions) {
    t17 = {
      label: "Tags",
      type: FilterType.MULTI_SELECT,
      component: "div",
      props: t15,
      schema: t16,
      options: tagOptions
    };
    $[26] = t15;
    $[27] = tagOptions;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  let t18;
  if ($[29] !== t14 || $[30] !== t17) {
    t18 = {
      type: t14,
      tags: t17
    };
    $[29] = t14;
    $[30] = t17;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  const filterConfigs = t18 satisfies Record<keyof FolderFilters, AnyLabeledFilterConfig>;
  let t19;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = {
      type: SelectFilter,
      tags: SelectFilter
    };
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  const filterComponents = t19;
  const selectedTypes = filters.type;
  const selectedTags = filters.tags;
  let t20;
  if ($[33] !== files || $[34] !== search || $[35] !== selectedTags || $[36] !== selectedTypes) {
    let t21;
    if ($[38] !== search || $[39] !== selectedTags || $[40] !== selectedTypes) {
      t21 = file_3 => {
        if (selectedTypes.length > 0 && !selectedTypes.includes(file_3.type)) {
          return false;
        }
        if (selectedTags.length > 0) {
          const matchesNoTags = selectedTags.includes(noTagsFilterValue) && file_3.tags.length === 0;
          const matchesTag = file_3.tags.some(tag_0 => selectedTags.includes(tag_0));
          if (!matchesNoTags && !matchesTag) {
            return false;
          }
        }
        return fileMatchesSearch(file_3, search);
      };
      $[38] = search;
      $[39] = selectedTags;
      $[40] = selectedTypes;
      $[41] = t21;
    } else {
      t21 = $[41];
    }
    t20 = files.filter(t21);
    $[33] = files;
    $[34] = search;
    $[35] = selectedTags;
    $[36] = selectedTypes;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  const filteredFiles = t20;
  let t21;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = event => {
      setSearch(event.target.value);
    };
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  let t22;
  if ($[43] !== search) {
    t22 = <SearchInput className="w-80" placeholder="Search files..." value={search} onChange={t21} onSearch={setSearch} />;
    $[43] = search;
    $[44] = t22;
  } else {
    t22 = $[44];
  }
  let t23;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = {};
    $[45] = t23;
  } else {
    t23 = $[45];
  }
  let t24;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = nextFilters => setFilters(normalizeFolderFilters(nextFilters));
    $[46] = t24;
  } else {
    t24 = $[46];
  }
  let t25;
  if ($[47] !== filterConfigs || $[48] !== filters || $[49] !== search) {
    t25 = <ListHeaderControls filterConfigs={filterConfigs} filterComponents={filterComponents} filterInput={t23} currentFilters={filters} currentSearch={search} onFilterChange={t24} showActiveClauses={true} wrapWithParent={true} />;
    $[47] = filterConfigs;
    $[48] = filters;
    $[49] = search;
    $[50] = t25;
  } else {
    t25 = $[50];
  }
  let t26;
  if ($[51] !== t22 || $[52] !== t25) {
    t26 = <>{t22}{t25}</>;
    $[51] = t22;
    $[52] = t25;
    $[53] = t26;
  } else {
    t26 = $[53];
  }
  const tools = t26;
  let t27;
  if ($[54] !== canManage || $[55] !== organizationMembershipId || $[56] !== schoolId || $[57] !== utils) {
    t27 = canManage && (organizationMembershipId || schoolId) ? <UploadFileTrigger organizationMembershipId={organizationMembershipId} schoolId={schoolId} accept="*" purpose={organizationMembershipId && !schoolId ? "folder" : undefined} onUpload={async () => {
      await utils.file.listFiles.invalidate({
        organizationMembershipId,
        schoolId
      });
      toast.success("File uploaded");
    }}>{_temp7}</UploadFileTrigger> : null;
    $[54] = canManage;
    $[55] = organizationMembershipId;
    $[56] = schoolId;
    $[57] = utils;
    $[58] = t27;
  } else {
    t27 = $[58];
  }
  const uploadAction = t27;
  let t28;
  let t29;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = [{
      id: "name",
      header: "Name",
      accessorKey: "name",
      size: 280,
      cell: _temp8
    }, {
      id: "type",
      header: "Type",
      accessorKey: "type",
      size: 120
    }, {
      id: "tags",
      header: "Tags",
      accessorKey: "tags",
      size: 120,
      cell: _temp9
    }, {
      id: "createdAt",
      header: "Created At",
      accessorKey: "createdAt",
      size: 120,
      cell: _temp10
    }];
    t29 = row_3 => {
      setSelectedFile(row_3);
    };
    $[59] = t28;
    $[60] = t29;
  } else {
    t28 = $[59];
    t29 = $[60];
  }
  let t30;
  if ($[61] !== canManage || $[62] !== deleteFileMutation) {
    t30 = row_4 => [...(canManage ? [{
      title: "Edit tags",
      onClick: _temp11
    }] : []), {
      title: "Download",
      onClick: async () => {
        await multiDownload([row_4.downloadUrl ?? row_4.url]);
      }
    }, ...(canManage ? [{
      title: "Delete",
      onClick: () => {
        toast.promise(deleteFileMutation.mutateAsync({
          fileId: row_4.id
        }), {
          loading: "Deleting file...",
          success: "File deleted",
          error: "Failed to delete file"
        });
      },
      isDestructive: true
    }] : [])];
    $[61] = canManage;
    $[62] = deleteFileMutation;
    $[63] = t30;
  } else {
    t30 = $[63];
  }
  let t31;
  if ($[64] !== bulkDelete.selectActions || $[65] !== filteredFiles || $[66] !== isLoading || $[67] !== search || $[68] !== t30) {
    t31 = <FilterList selectActions={bulkDelete.selectActions} data={filteredFiles} isLoading={isLoading} currentSearch={search} toolbarPlacement="external" columns={t28} onClickRow={t29} actionMenuItems={t30} />;
    $[64] = bulkDelete.selectActions;
    $[65] = filteredFiles;
    $[66] = isLoading;
    $[67] = search;
    $[68] = t30;
    $[69] = t31;
  } else {
    t31 = $[69];
  }
  const fileList = t31;
  let t32;
  if ($[70] !== selectedFile) {
    t32 = selectedFile ? <FileDisplayDialog file={selectedFile} setFile={setSelectedFile} /> : null;
    $[70] = selectedFile;
    $[71] = t32;
  } else {
    t32 = $[71];
  }
  const fileDialog = t32;
  if (title) {
    let t33;
    if ($[72] !== fileList || $[73] !== title || $[74] !== tools || $[75] !== uploadAction) {
      t33 = <ListPage title={title} actions={uploadAction} tools={tools}>{fileList}</ListPage>;
      $[72] = fileList;
      $[73] = title;
      $[74] = tools;
      $[75] = uploadAction;
      $[76] = t33;
    } else {
      t33 = $[76];
    }
    let t34;
    if ($[77] !== bulkDelete.dialog || $[78] !== fileDialog || $[79] !== t33) {
      t34 = <>{t33}{fileDialog}{bulkDelete.dialog}</>;
      $[77] = bulkDelete.dialog;
      $[78] = fileDialog;
      $[79] = t33;
      $[80] = t34;
    } else {
      t34 = $[80];
    }
    return t34;
  }
  let t33;
  if ($[81] !== fileList || $[82] !== tools || $[83] !== uploadAction) {
    t33 = <TabListSection tools={tools} actions={uploadAction}>{fileList}</TabListSection>;
    $[81] = fileList;
    $[82] = tools;
    $[83] = uploadAction;
    $[84] = t33;
  } else {
    t33 = $[84];
  }
  let t34;
  if ($[85] !== bulkDelete.dialog || $[86] !== fileDialog || $[87] !== t33) {
    t34 = <>{t33}{fileDialog}{bulkDelete.dialog}</>;
    $[85] = bulkDelete.dialog;
    $[86] = fileDialog;
    $[87] = t33;
    $[88] = t34;
  } else {
    t34 = $[88];
  }
  return t34;
}
function _temp11() {}
function _temp10(t0) {
  const {
    row: row_2
  } = t0;
  return <DateDisplay date={row_2.original.createdAt} />;
}
function _temp9(t0) {
  const {
    row: row_1
  } = t0;
  return row_1.original.tags.length > 0 ? <div className="flex flex-row gap-2">{row_1.original.tags.join(", ")}</div> : <span className="text-text-tertiary">None</span>;
}
function _temp8(t0) {
  const {
    row: row_0
  } = t0;
  return <div className="flex min-w-0 items-center gap-2.5"><FileTypeIcon name={row_0.original.name} type={row_0.original.type} mimetype={row_0.original.mimetype} /><span className="line-clamp-1">{row_0.original.name}</span></div>;
}
function _temp7(t0) {
  const {
    open
  } = t0;
  return <Button onClick={open}>Upload</Button>;
}
function _temp6(file_2) {
  return file_2.tags.length === 0;
}
function _temp5(file_1) {
  return file_1.tags;
}
function _temp4(file_0) {
  return file_0.type;
}
function _temp3(row) {
  return row.id;
}
function _temp2(file) {
  return {
    ...file,
    tags: file.tags.filter(_temp)
  };
}
function _temp(tag) {
  return tag !== "folder-upload";
}